import { nations } from './constants';
import $ from 'jquery';
import numeral from 'numeral';
import 'numeral/locales/it';
import 'numeral/locales/en-gb';
import 'numeral/locales/en-au';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { mergeMap, toArray } from 'rxjs/operators';
import { from, Observable, of } from 'rxjs';
import * as moment from 'moment';
import { Moment } from 'moment';

const convertToKebabCase = str => {
  return str
    .replace(/([a-z])([A-Z])/g, '$1-$2') // get all lowercase letters that are near to uppercase ones
    .replace(/[\s_]+/g, '-') // replace all spaces and low dash
    .toLowerCase(); // convert to lower case
};

const autonumericDecimalOptions = () => {
  const options = {
    digitGroupSeparator: '.',
    decimalCharacter: ',',
    decimalCharacterAlternative: '.',
    decimalPlaces: 2,
    allowDecimalPadding: 'floats'
  };

  return options;
};

export function decodeLanguage(lang: string): string {
  let decodedLang;
  switch (lang) {
    case 'it':
      decodedLang = 'ita';
      break;
    case 'en':
      decodedLang = 'eng';
      break;
    case 'fr':
      decodedLang = 'fra';
      break;
    default:
      decodedLang = 'eng';
      break;
  }
  return decodedLang;
}

export function removeEmptyProperties(obj: object, emptyStringIsNull: boolean = true): object {
  const newObj = {};

  Object.keys(obj).forEach(key => {
    if (obj[key] && typeof obj[key] === 'object') {
      newObj[key] = removeEmptyProperties(obj[key]); // recurse
    } else if (emptyStringIsNull && obj[key] === '') {
    } else if (obj[key] !== null) {
      newObj[key] = obj[key]; // copy value
    }
  });

  return newObj;
}

export function flattenObject(obj: object, removeEmpty: boolean = false): object {
  let newobj = JSON.parse(JSON.stringify(obj));
  if (removeEmpty) {
    newobj = removeEmptyProperties(newobj);
  }
  // Fix dates from iso with milliseconds to simple format, NON RECURSIVE
  for (const key in newobj) {
    if (newobj.hasOwnProperty(key)) {
      if (obj[key] instanceof moment) {
        if (moment.isMoment(obj[key])) {
          newobj[key] = formatMomentForJava(obj[key].utc());
        }
      }
      if (obj[key] instanceof Date) {
        newobj[key] = formatMomentForJava(moment(obj[key]));
      }
    }
  }
  return newobj;
}

export function formatMomentForJava(date: Moment): string {
  return date.utc().format('YYYY-MM-DDTHH:mm:ss') + '+0000';
}

export function getNation3To2(code: string) {
  const nation = nations.find(x => x[0] === code);
  if (nation) {
    return nation[3];
  }
  return null;
}

export function getNation3FlagUrl(code: string) {
  const nation = nations.find(x => x[0] === code);
  if (nation) {
    const nationCode = nation[3];
    return 'assets/images/icons/flags/' + nationCode + '.svg';
  }
  return null;
}

export function getNationByIso3166Code(code: string) {
  return nations.find(x => x[3].toLowerCase() === code.toLowerCase());
}

export { convertToKebabCase, autonumericDecimalOptions };

/**
 * Simple object check.
 */
export function isObject(item) {
  return item && typeof item === 'object' && !Array.isArray(item);
}

export function clone(source) {
  return Object.assign({}, source);
}

export function extend(target, source) {
  return $.extend(target, source);
}

export function extendDeep(target, source) {
  return $.extend(true, target, source);
}

export function getCurrencySymbol() {
  let symbol = (window as any).sessionStorage.getItem('wsb-preferences');
  symbol = symbol ? JSON.parse(symbol) : {};
  return symbol ? symbol.currency : '';
}

export function formatIntNumber(num) {
  return numeral(num).format('0,0');
}

export function getDecimalSeparator() {
  return numeral.localeData().delimiters.decimal;
}

export function formatNumber(num) {
  return numeral(num).format('0,0.00');
}

export function setNumeralLocaleByNation(nation) {
  // https://github.com/adamwdraper/Numeral-js/tree/master/locales
  if (['ita', 'deu', 'fra', 'esp'].indexOf(nation) !== -1) {
    numeral.locale('it');
  } else if (['aus'].indexOf(nation) !== -1) {
    numeral.locale('en-au');
  } else {
    numeral.locale('en-gb');
  }
}

export function formatCurrency(price, opts: any = {}) {
  // numeral.locale('it');
  let val = price;
  if (opts.keepDecimals) {
    val = numeral(val).format('0,0.00[00000]');
  } else {
    val = numeral(val).format('0,0.00');
  }
  if (opts.showSymbol) {
    val = getCurrencySymbol() + ' ' + val;
  }
  return val;
}

export function formateDateTime(date, showTime = true) {
  let symbol = (window as any).sessionStorage.getItem('wsb-preferences');
  symbol = symbol ? JSON.parse(symbol) : {};
  const format = symbol.dateFormat === 'dd-MM-yyyy' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';
  return moment(date).format(format + (showTime ? ' ' + symbol.hourFormat : ''));
}

export function formateDateTimeWithSeconds(date, showTime = true) {
  let symbol = (window as any).sessionStorage.getItem('wsb-preferences');
  symbol = symbol ? JSON.parse(symbol) : {};
  const formatDate = symbol.dateFormat === 'dd-MM-yyyy' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';
  const formatTime = symbol.hourFormat === 'HH:mm' ? 'HH:mm:ss' : 'hh:mm:ss a';
  return moment(date).format(formatDate + (showTime ? ' ' + formatTime : ''));
}

export function unique(array, propertyName) {
  return array.filter((e, i) => array.findIndex(a => a[propertyName] === e[propertyName]) === i);
}

export function uniqueIncreasing() {
  if (!(window as any).uniqueIncreasing) {
    (window as any).uniqueIncreasing = 1;
  }
  (window as any).uniqueIncreasing++;
  return (window as any).uniqueIncreasing;
}

export function validateFormGroup(formGroup: UntypedFormGroup) {
  setTimeout(() => {
    // eslint-disable-next-line guard-for-in
    for (const key in formGroup.controls) {
      const formElement = formGroup.get(key);
      if (formElement instanceof UntypedFormControl) {
        formElement.updateValueAndValidity();
      } else if (formElement instanceof UntypedFormGroup) {
        validateFormGroup(formGroup);
      }
    }
  });
}

export function limitedParallelObservableExecution<T>(
  listOfItems: Array<T>,
  observableMethod: (item: T) => Observable<any>,
  maxConcurrency: number = 4
): Observable<any> {
  if (listOfItems && listOfItems.length > 0) {
    const observableListOfItems: Observable<T> = from(listOfItems);
    return observableListOfItems.pipe(mergeMap(observableMethod, maxConcurrency), toArray());
  } else {
    return of({});
  }
}

export function getSubscriptionStatus(expireDate) {
  const today = moment().format('YYYY-MM-DD');
  const remainigDays = moment(expireDate).diff(today, 'days');
  if (remainigDays <= 10 && remainigDays >= 0) {
    return 'expiring';
  } else if (remainigDays < 0) {
    return 'expired';
  } else {
    return 'active';
  }
}

/**
 * Not a real util, should be removed later, PLT-4340
 */
export function enableEmailInExport() {
  return false;
}
/**
 * Not a real util, should be removed later, PLT-4340
 */
export function enableEditMailAccount() {
  return false;
}

export enum FilterKey {
  PERIOD = 'period',
  FROM_DATE = 'fromDate',
  FROM_TIME = 'fromTime',
  TO_DATE = 'toDate',
  TO_TIME = 'toTime',
  RECIPIENT = 'recipient'
}

export enum BrandName {
  MOBYTFR = 'mobytfr',
  SMSENVOI = 'smsenvoi',
  SKEBBY = 'skebby',
  MEDIABURST = 'mediaburst',
  ESENDEXAU = 'esendexau',
  VODAFONE = 'vodafone',
  ARUBA = 'aruba',
  MOBYT = 'mobyt'
}
